import { useEffect, useRef, useState } from "react";
import { CircularProgress } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { toast } from "react-toastify";
import RightArrow from "../../../assets/rightArrowWhite.svg";
import { getUser } from "../../../config/user";
import { PatientServices } from "../../../services/PatientServices";
import { CaseServices } from "../../../services/CaseServices";
import { handleApiErrorResponses } from "../../../utils/ApisErrorHandling";
import { ProcessingServices } from "../../../services/ProcessingServices";
import { pdfjs } from "react-pdf";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const isPasswordProtectedPdf = async (file) => {
  const isPdf = file.type === "application/pdf" || /\.pdf$/i.test(file.name);
  if (!isPdf) return false;

  try {
    const data = await file.arrayBuffer();
    // Fast path: encrypted PDFs usually contain an /Encrypt dictionary
    const bytes = new Uint8Array(data);
    const decoder = new TextDecoder("latin1");
    const chunkSize = 65536;
    for (let offset = 0; offset < bytes.length; offset += chunkSize) {
      const start = Math.max(0, offset - 16);
      const end = Math.min(offset + chunkSize, bytes.length);
      if (decoder.decode(bytes.subarray(start, end)).includes("/Encrypt")) {
        return true;
      }
    }

    // Confirm with pdf.js (throws PasswordException for protected files)
    await pdfjs.getDocument({ data, password: "" }).promise;
    return false;
  } catch (error) {
    const needsPassword =
      error?.name === "PasswordException" ||
      error?.code === 1 ||
      error?.code === 2 ||
      /password/i.test(error?.message || "");
    return needsPassword;
  }
};

// TODO: replace with the response of the insurance card upload API
const DUMMY_PATIENT_DETAILS = {
  payer: "TATA AIG MEDICARE SELECT",
  patientName: "Gajendra Singh",
  memberId: "IDV00351641901034",
  policyId: "10176416291212",
  age: "34 Years",
  gender: "Male",
};

const PATIENT_FIELDS = [
  { name: "payer", label: "Payer/TPA" },
  { name: "patientName", label: "Patient Name" },
  { name: "memberId", label: "Member ID" },
  { name: "policyId", label: "Policy ID" },
  { name: "age", label: "Age" },
];

const GENDER_OPTIONS = ["Male", "Female", "Other"];

// Patient name may contain only letters and spaces
const getNameError = (value) => {
  const text = (value ?? "").toString();
  if (!text.trim()) return "Patient name is required";
  if (!/^[A-Za-z ]+$/.test(text))
    return "Patient name can contain only letters and spaces";
  return "";
};

const MIN_AGE = 0;
const MAX_AGE = 130;

// Returns an error message for an invalid age, or "" when the age is valid.
// Accepts values like 34, "34" or "34 Years" (as returned by the card reader).
const getAgeError = (value) => {
  const text = (value ?? "").toString().trim();
  if (!text) return "Age is required";

  const match = text.match(/^(\d+)(?:\s*(?:years?|yrs?|y))?$/i);
  if (!match) return "Please enter a valid age";

  const age = parseInt(match[1], 10);
  if (age < MIN_AGE) return `Age must be at least ${MIN_AGE} years`;
  if (age > MAX_AGE) return `Age must not be more than ${MAX_AGE} years`;
  return "";
};

const labelClassName =
  "font-[Lufga] font-normal text-[14px] leading-[100%] -tracking-[0.36px] text-[#000000]";

const inputClassName =
  "w-full h-[50px] box-border p-[16px] rounded-[26.5px] border-[0.5px] border-solid border-[#017C71] bg-transparent font-[Lufga] font-normal text-[14px] leading-[100%] -tracking-[0.36px] capitalize text-[#000000] outline-none";

const AddPatientModal = ({ onClose, files, setFiles, AllCases }) => {
  const aadhaarInputRef = useRef(null);
  const medicalInputRef = useRef(null);
  const [aadhaarCard, setAadhaarCard] = useState(null);
  const [medicalCard, setMedicalCard] = useState(null);
  const uploadTimerRef = useRef(null);
  const [step, setStep] = useState("upload");
  const [uploading, setUploading] = useState(false);
  const [patientDetails, setPatientDetails] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const user = getUser();
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
      clearTimeout(uploadTimerRef.current);
    };
  }, []);

  // TODO: replace the timeout with the real upload API call
  const uploadDocuments = (selectedFiles) =>
    new Promise((resolve) => {
      uploadTimerRef.current = setTimeout(
        () => resolve({ files: selectedFiles, details: DUMMY_PATIENT_DETAILS }),
        1500,
      );
    });

  // Validate a selected file; returns true when the file can be used
  const validateFile = async (file) => {
    if (file.size === 0) {
      toast.error(
        "The uploaded file is empty. Please upload a valid health card document.",
      );
      return false;
    }

    // 5 MB max (5242880 bytes)
    if (file.size > 5242880) {
      window.alert("Please upload a file smaller than 5 MB");
      return false;
    }

    // Allowed types and extensions
    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "application/pdf",
    ];
    const allowedExtensions = /\.(jpe?g|png|pdf)$/i;

    const isValidType =
      allowedTypes.includes(file.type) && allowedExtensions.test(file.name);

    if (!isValidType) {
      toast.error("Only JPG, JPEG, PNG, and PDF files are allowed");
      return false;
    }

    if (await isPasswordProtectedPdf(file)) {
      toast.error(
        "Password-protected files are not allowed. Please upload an unprotected file.",
      );
      return false;
    }

    return true;
  };

  // Calls the processing API only when both Aadhaar card and patient card are available
  const readPatientCards = async (aadhaar, medical) => {
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append("aadhaar_card", aadhaar);
      formData.append("medical_card", medical);

      const response = await ProcessingServices.uploadPatientCards(formData);

      console.log("response data", response.data);
      if (response && response.data) {
        const aadhaarData = response.data.aadhaar_card || {};
        const medicalData = response.data.medical_card || {};

        // Match the API gender value (e.g. "MALE", "f") to the dropdown options
        const rawGender = (medicalData.gender || aadhaarData.gender || "")
          .toString()
          .trim();
        const gender =
          GENDER_OPTIONS.find(
            (option) =>
              option.toLowerCase() === rawGender.toLowerCase() ||
              option[0].toLowerCase() === rawGender.toLowerCase(),
          ) || rawGender;

        setPatientDetails({
          patientName: medicalData.insured_name || aadhaarData.name,
          payer: medicalData.insurance_company,
          memberId: medicalData.member_id,
          employeeId: medicalData.employee_id,
          policyId: medicalData.policy_number,
          gender,
          age: medicalData.age,
          validFrom: medicalData.valid_from,
          validTo: medicalData.valid_to,
          dateOfBirth: aadhaarData.date_of_birth,
          address: aadhaarData.address,
        });

        if (medicalData?.member_id || medicalData?.policy_number) {
          if (medicalData.is_valid === false) {
            toast.warn("The patient card appears to be invalid or expired.");
          }
          setStep("details");
        } else {
          toast.error("Please upload a valid patient card.");
        }
      } else {
        toast.error("No patient details extracted from cards.");
        // setStep("details");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail);
      console.log("error", err?.response?.data?.detail);
      setStep("upload");
    } finally {
      setUploading(false);
    }
  };

  // type: "aadhaar" | "medical"
  const handleFileChange = async (e, type) => {
    const file = e.target.files && e.target.files[0];
    // Reset input so selecting the same file again still triggers onChange
    e.target.value = "";

    if (!file) return;
    if (!(await validateFile(file))) return;

    const nextAadhaar = type === "aadhaar" ? file : aadhaarCard;
    const nextMedical = type === "medical" ? file : medicalCard;

    if (type === "aadhaar") setAadhaarCard(file);
    else setMedicalCard(file);
    setFiles([nextAadhaar, nextMedical].filter(Boolean));

    // Only call the API once both cards have been uploaded
    if (nextAadhaar && nextMedical) {
      await readPatientCards(nextAadhaar, nextMedical);
    }
  };

  const handleRemoveFile = (type) => {
    const nextAadhaar = type === "aadhaar" ? null : aadhaarCard;
    const nextMedical = type === "medical" ? null : medicalCard;
    setAadhaarCard(nextAadhaar);
    setMedicalCard(nextMedical);
    setFiles([nextAadhaar, nextMedical].filter(Boolean));
  };

  const handleDetailChange = (e) => {
    const { name } = e.target;
    let { value } = e.target;
    // Block anything other than letters and spaces in the patient name
    if (name === "patientName") value = value.replace(/[^A-Za-z ]/g, "");
    setPatientDetails((prev) => ({ ...prev, [name]: value }));
  };

  const handleCloseAndReset = () => {
    setFiles([]);
    setAadhaarCard(null);
    setMedicalCard(null);
    onClose();
  };

  // TODO: replace with the real add patient API call
  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError =
      getNameError(patientDetails?.patientName) ||
      getAgeError(patientDetails?.age);
    if (validationError) {
      toast.error(validationError);
      return;
    }
    setSubmitting(true);
    const data = {
      hospitalId: user?.hospital?.id,
      beneficiaryNo: patientDetails?.memberId || "",
      patientName: patientDetails?.patientName,
      // email: "",
      // phone: "",
      gender: patientDetails?.gender,
      age: patientDetails?.age,
      // state: "",
      // city: "",
      registeredDate: new Date().toISOString(),
      pmjayRegistrationId: patientDetails?.policyId,

      // carePlan: "",
      // birthDate: null,
    };
    try {
      const response = await PatientServices.addPatientDetails(data);
      if (response && response.data && response.data.patientId) {
        await CaseServices.addNewCase({
          patientId: response.data.patientId,
          hospitalId: user?.hospital?.id,
          pmjayRegistrationId: patientDetails?.policyId,
          policyId: patientDetails?.policyId,
          // carePlan: values?.carePlan,
          payer: patientDetails?.payer,
          status: "INACTIVE",
          isTpa: 1,
        });

        toast.success("Case Added");
        AllCases();
        handleCloseAndReset();
      } else {
        throw new Error("Invalid response or missing patientId");
      }
    } catch (error) {
      handleApiErrorResponses(error);
    } finally {
      setSubmitting(false);
    }
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="fixed top-0 left-0 h-screen w-screen flex justify-center items-center bg-[#017C7180] z-10">
      <div
        style={{ border: "1px solid #FFFFFF" }}
        className={`relative max-w-[90vw] max-h-[90vh] box-border overflow-y-auto flex flex-col items-center ${
          step === "upload"
            ? "w-[920px] min-h-[272px] p-[24px] gap-[60px] rounded-[24px] bg-[#FFFFFFCC] backdrop-blur-[40px]"
            : "w-[640px] p-[18px] gap-[16px] rounded-[32px] bg-[#FFFFFF]"
        }`}
      >
        {!uploading && (
          <button
            type="button"
            className={`absolute bg-transparent border-0 cursor-pointer ${
              step === "upload"
                ? "top-[24px] right-[24px]"
                : "top-[18px] right-[18px]"
            }`}
            onClick={step === "details" ? handleCloseAndReset : onClose}
          >
            <CloseIcon sx={{ fontSize: 28, color: "#000000" }} />
          </button>
        )}

        {step === "upload" ? (
          <>
            <div className="flex flex-col items-center gap-[24px]">
              <div className="font-[Lufga] font-medium text-[42px] leading-[155%] -tracking-[0.07em] text-center align-middle text-[#000000]">
                Add Patient
              </div>
              <div className="font-[Lufga] font-light text-[16px] leading-[100%] tracking-normal text-center align-middle text-[#000000]">
                Upload Patient's Aadhaar card and Health Insurance card.
              </div>
            </div>

            <input
              ref={aadhaarInputRef}
              type="file"
              accept=".jpg,.jpeg,.png,.pdf"
              className="hidden"
              onChange={(e) => handleFileChange(e, "aadhaar")}
            />
            <input
              ref={medicalInputRef}
              type="file"
              accept=".jpg,.jpeg,.png,.pdf"
              className="hidden"
              onChange={(e) => handleFileChange(e, "medical")}
            />
            {uploading ? (
              <div className="flex flex-col items-center gap-[12px]">
                <CircularProgress sx={{ color: "#000000" }} />
                <div className="font-[Lufga] text-[14px] text-[#000000]">
                  Uploading documents...
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap justify-center gap-[16px]">
                <button
                  type="button"
                  className="flex items-center gap-[16px] px-[32px] py-[16px] rounded-full bg-[#000000] font-[Lufga] font-normal text-[12px] leading-[100%] -tracking-[0.36px] text-center text-[#FFFFFF] uppercase border-0 cursor-pointer"
                  onClick={() => aadhaarInputRef.current?.click()}
                >
                  <FileUploadOutlinedIcon sx={{ fontSize: 20 }} />
                  {aadhaarCard ? "Replace Aadhaar Card" : "Upload Aadhaar Card"}
                </button>
                <button
                  type="button"
                  className="flex items-center gap-[16px] px-[32px] py-[16px] rounded-full bg-[#000000] font-[Lufga] font-normal text-[12px] leading-[100%] -tracking-[0.36px] text-center text-[#FFFFFF] uppercase border-0 cursor-pointer"
                  onClick={() => medicalInputRef.current?.click()}
                >
                  <FileUploadOutlinedIcon sx={{ fontSize: 20 }} />
                  {medicalCard
                    ? "Replace Health Insurance Card"
                    : "Upload Health Insurance Card"}
                </button>
              </div>
            )}

            {(aadhaarCard || medicalCard) && (
              <div className="w-full flex flex-col gap-[12px]">
                <div className="font-[Lufga] text-[16px] text-[#000000]">
                  Uploaded Documents (
                  {[aadhaarCard, medicalCard].filter(Boolean).length})
                </div>
                <div className="flex flex-wrap gap-[12px]">
                  {[
                    {
                      type: "aadhaar",
                      label: "Aadhaar Card",
                      file: aadhaarCard,
                    },
                    {
                      type: "medical",
                      label: "Patient Card",
                      file: medicalCard,
                    },
                  ]
                    .filter((item) => item.file)
                    .map(({ type, label, file }) => (
                      <div
                        key={type}
                        style={{ border: "1px solid #FFFFFF" }}
                        className="flex items-center gap-[10px] max-w-[320px] px-[16px] py-[8px] rounded-full bg-[#FFFFFF80]"
                      >
                        <InsertDriveFileOutlinedIcon
                          sx={{ fontSize: 18, color: "#000000" }}
                        />
                        <div className="min-w-0 flex flex-col">
                          <span className="font-[Lufga] text-[11px] text-[#017C71]">
                            {label}
                          </span>
                          <span
                            className="font-[Lufga] text-[13px] text-[#000000] truncate"
                            title={file.name}
                          >
                            {file.name}
                          </span>
                          <span className="font-[Lufga] text-[11px] text-[#00000080]">
                            {formatSize(file.size)}
                          </span>
                        </div>
                        {!uploading && (
                          <button
                            type="button"
                            className="flex bg-transparent border-0 cursor-pointer p-0"
                            onClick={() => handleRemoveFile(type)}
                          >
                            <CloseIcon
                              sx={{ fontSize: 16, color: "#000000" }}
                            />
                          </button>
                        )}
                      </div>
                    ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <form
            className="w-full flex flex-col items-center gap-[16px]"
            onSubmit={handleSubmit}
          >
            <div className="flex flex-col items-center">
              <div className="font-[Lufga] font-normal text-[42px] leading-[54px] -tracking-[4px] text-center align-bottom text-[#000000]">
                Add Patient
              </div>
              <div className="font-[Lufga] font-normal text-[14px] leading-[54px] tracking-[0px] text-center align-middle text-[#00000066]">
                Please make sure they have all been successfully uploaded.
              </div>
            </div>

            <div className="w-full h-0 border-0 border-t border-solid border-[#E8E8E8]" />

            <div className="w-full grid grid-cols-[260px_260px] justify-between gap-y-[16px]">
              {PATIENT_FIELDS.map((field) => {
                const fieldError =
                  field.name === "age"
                    ? getAgeError(patientDetails.age)
                    : field.name === "patientName"
                      ? getNameError(patientDetails.patientName)
                      : "";
                return (
                  <label key={field.name} className="flex flex-col gap-[8px]">
                    <span className={labelClassName}>{field.label}</span>
                    <input
                      type="text"
                      name={field.name}
                      value={patientDetails[field.name] || ""}
                      onChange={handleDetailChange}
                      className={inputClassName}
                      style={
                        fieldError ? { borderColor: "#D32F2F" } : undefined
                      }
                    />
                    {fieldError && (
                      <span className="font-[Lufga] text-[12px] text-[#D32F2F]">
                        {fieldError}
                      </span>
                    )}
                  </label>
                );
              })}

              <label className="flex flex-col gap-[8px]">
                <span className={labelClassName}>Gender</span>
                <div className="relative">
                  <select
                    name="gender"
                    value={patientDetails.gender || ""}
                    onChange={handleDetailChange}
                    className={`${inputClassName} pr-[44px] appearance-none cursor-pointer`}
                  >
                    {GENDER_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                  <KeyboardArrowDownIcon
                    sx={{ fontSize: 22, color: "#000000" }}
                    className="absolute right-[16px] top-1/2 -translate-y-1/2 pointer-events-none"
                  />
                </div>
              </label>
            </div>

            {submitting ? (
              <CircularProgress sx={{ color: "#000000" }} />
            ) : (
              <button
                type="submit"
                style={{ border: "1px solid #017C71" }}
                className="flex items-center justify-center gap-[16px] w-[160px] h-[51px] box-border p-[16px] rounded-[100px] bg-[#017C71] font-[Lufga] font-medium text-[12px] leading-[155%] -tracking-[0.07em] text-center align-middle text-[#FFFFFF] cursor-pointer"
              >
                Submit
                <img src={RightArrow} alt="arrow-icon" />
              </button>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

export default AddPatientModal;
